import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { BankGuidanceCard } from '../components/BankGuidance';
import { adminApi } from '../services/api';
import type { AdminBank, BankGuidancePreview, EnglishGuidanceStatus } from '../types';
import { saveErrorText } from '../utils/apiError';

export const ENGLISH_STATUS_BADGE: Record<EnglishGuidanceStatus, string> = {
  missing: 'bg-secondary',
  draft: 'bg-info text-dark',
  published: 'bg-success',
  stale: 'bg-warning text-dark'
};

export function EnglishStatusBadge({ status }: { status: EnglishGuidanceStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${ENGLISH_STATUS_BADGE[status]}`} data-testid="admin-bank-guide-en-status">
      {t(`adminBankGuide.enStatus.${status}`)}
    </span>
  );
}

type SwedishForm = { guidanceTextSv: string; raiseLimitSelfPathSv: string; infoUrl: string; supportPhone: string };
type EnglishForm = { guidanceTextEnDraft: string; raiseLimitSelfPathEnDraft: string };

const swedishFormFor = (bank: AdminBank): SwedishForm => ({
  guidanceTextSv: bank.guidanceTextSv,
  raiseLimitSelfPathSv: bank.raiseLimitSelfPathSv ?? '',
  infoUrl: bank.infoUrl ?? '',
  supportPhone: bank.supportPhone ?? ''
});

// The form starts from the draft when there is one, else the published English.
const englishFormFor = (bank: AdminBank): EnglishForm =>
  bank.guidanceEnDraftUpdatedAt
    ? { guidanceTextEnDraft: bank.guidanceTextEnDraft ?? '', raiseLimitSelfPathEnDraft: bank.raiseLimitSelfPathEnDraft ?? '' }
    : { guidanceTextEnDraft: bank.guidanceTextEn, raiseLimitSelfPathEnDraft: bank.raiseLimitSelfPathEn ?? '' };

const httpsUrl = (value: string) => !value.trim() || /^https:\/\/\S+$/i.test(value.trim());

/**
 * Konfiguration → Banker → bankguide (ClickUp 869eu8fd9): the Swedish guide,
 * the English as a draft that Admin publishes, and a preview of what a
 * Beställare sees. The English is written by hand for now.
 */
export function AdminBankGuide() {
  const { t, i18n } = useTranslation();
  const { bankId = '' } = useParams<{ bankId: string }>();
  const [bank, setBank] = useState<AdminBank | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [swedish, setSwedish] = useState<SwedishForm | null>(null);
  const [english, setEnglish] = useState<EnglishForm | null>(null);
  const [previewLanguage, setPreviewLanguage] = useState<'sv' | 'en'>('sv');
  const [preview, setPreview] = useState<BankGuidancePreview | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [busy, setBusy] = useState<'sv' | 'en' | 'publish' | null>(null);
  const [triedSwedish, setTriedSwedish] = useState(false);

  const show = (saved: AdminBank) => {
    setBank(saved);
    setSwedish(swedishFormFor(saved));
    setEnglish(englishFormFor(saved));
  };

  useEffect(() => {
    adminApi
      .getBanks()
      .then(banks => {
        const found = banks.find(item => item.id === bankId);
        if (found) show(found);
        else setLoadError(true);
      })
      .catch(() => setLoadError(true));
  }, [bankId]);

  const loadPreview = useCallback(() => {
    if (!bankId) return;
    adminApi.getBankGuidancePreview(bankId, previewLanguage).then(setPreview).catch(() => setPreview(null));
  }, [bankId, previewLanguage]);

  useEffect(() => {
    if (bank) loadPreview();
  }, [bank, loadPreview]);

  const dateTime = (value: string | null) =>
    value
      ? new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE', {
          dateStyle: 'short',
          timeStyle: 'short'
        }).format(new Date(value))
      : t('adminBankGuide.never');

  const saveSwedish = async () => {
    if (!bank || !swedish) return;
    setTriedSwedish(true);
    if (!httpsUrl(swedish.infoUrl)) return;
    setBusy('sv');
    setMessage(null);
    try {
      show(await adminApi.updateBank(bank.id, {
        guidanceTextSv: swedish.guidanceTextSv,
        raiseLimitSelfPathSv: swedish.raiseLimitSelfPathSv,
        infoUrl: swedish.infoUrl,
        supportPhone: swedish.supportPhone
      }));
      setMessage({ type: 'success', text: t('adminBankGuide.savedSwedish') });
    } catch (error) {
      setMessage({ type: 'danger', text: saveErrorText(t, error, 'adminBankGuide.saveError') });
    } finally {
      setBusy(null);
    }
  };

  const saveEnglishDraft = async () => {
    if (!bank || !english) return null;
    return adminApi.updateBank(bank.id, {
      guidanceTextEnDraft: english.guidanceTextEnDraft,
      raiseLimitSelfPathEnDraft: english.raiseLimitSelfPathEnDraft
    });
  };

  const englishChanged = !!bank && !!english &&
    JSON.stringify(english) !== JSON.stringify(englishFormFor(bank));

  const saveDraft = async () => {
    setBusy('en');
    setMessage(null);
    try {
      const saved = await saveEnglishDraft();
      if (saved) show(saved);
      setMessage({ type: 'success', text: t('adminBankGuide.savedDraft') });
    } catch (error) {
      setMessage({ type: 'danger', text: saveErrorText(t, error, 'adminBankGuide.saveError') });
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!bank) return;
    setBusy('publish');
    setMessage(null);
    try {
      if (englishChanged) await saveEnglishDraft();
      show(await adminApi.publishEnglishBankGuidance(bank.id));
      setMessage({ type: 'success', text: t('adminBankGuide.published') });
    } catch (error) {
      setMessage({ type: 'danger', text: saveErrorText(t, error, 'adminBankGuide.saveError') });
    } finally {
      setBusy(null);
    }
  };

  const status = bank?.guidanceEnStatus;
  const canPublish = !!english?.guidanceTextEnDraft.trim() && (englishChanged || status !== 'published');

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {bank ? t('adminBankGuide.title', { bank: bank.name }) : t('adminBankGuide.titleFallback')}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('appNav.configuration') },
            { label: t('adminBanks.title'), path: '/admin/system/banks' },
            { label: bank?.name ?? t('adminBankGuide.titleFallback') }
          ]} />
        </div>
      </div>

      {loadError && <div className="alert alert-danger" role="alert">{t('adminBankGuide.loadError')}</div>}
      {message && (
        <div className={`alert alert-${message.type} alert-dismissible`} role={message.type === 'danger' ? 'alert' : 'status'}>
          {message.text}
          <button type="button" className="btn-close" aria-label={t('common.close')} onClick={() => setMessage(null)}></button>
        </div>
      )}

      {bank && swedish && english && (
        <>
          <div className="row g-4 mb-4">
            <div className="col-12 col-lg-6">
              <Card className="h-100">
                <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-3">
                  <h2 className="h6 fw-bold mb-0">
                    {t('adminBankGuide.swedish')} <span className="text-muted fw-normal">· {t('adminBankGuide.sourceLanguage')}</span>
                  </h2>
                  <span className="small text-muted">{t('adminBankGuide.changedAt', { date: dateTime(bank.guidanceSvUpdatedAt) })}</span>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="bank-guide-sv-text">{t('adminBankGuide.guidanceText')}</label>
                  <textarea id="bank-guide-sv-text" className="form-control" rows={8} maxLength={4000}
                    value={swedish.guidanceTextSv} aria-describedby="bank-guide-sv-text-help"
                    onChange={event => setSwedish({ ...swedish, guidanceTextSv: event.target.value })} />
                  <div id="bank-guide-sv-text-help" className="form-text">{t('adminBankGuide.guidanceTextHelp')}</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="bank-guide-sv-path">{t('adminBankGuide.selfPath')}</label>
                  <textarea id="bank-guide-sv-path" className="form-control" rows={2} maxLength={500}
                    value={swedish.raiseLimitSelfPathSv} placeholder={t('adminBankGuide.selfPathPlaceholder')}
                    onChange={event => setSwedish({ ...swedish, raiseLimitSelfPathSv: event.target.value })} />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-12 col-sm-7">
                    <label className="form-label fw-semibold" htmlFor="bank-guide-url">{t('adminBankGuide.infoUrl')}</label>
                    <input id="bank-guide-url" type="url" className={`form-control ${triedSwedish && !httpsUrl(swedish.infoUrl) ? 'is-invalid' : ''}`}
                      value={swedish.infoUrl} maxLength={500} aria-describedby="bank-guide-url-help"
                      onChange={event => setSwedish({ ...swedish, infoUrl: event.target.value })} />
                    {triedSwedish && !httpsUrl(swedish.infoUrl)
                      ? <div className="invalid-feedback" data-testid="bank-guide-url-error">{t('adminBankGuide.infoUrlInvalid')}</div>
                      : <div id="bank-guide-url-help" className="form-text">{t('adminBankGuide.infoUrlHelp')}</div>}
                  </div>
                  <div className="col-12 col-sm-5">
                    <label className="form-label fw-semibold" htmlFor="bank-guide-phone">{t('adminBankGuide.supportPhone')}</label>
                    <input id="bank-guide-phone" type="tel" className="form-control" value={swedish.supportPhone} maxLength={40}
                      onChange={event => setSwedish({ ...swedish, supportPhone: event.target.value })} />
                  </div>
                  <div className="col-12 form-text mt-1">{t('adminBankGuide.sharedHelp')}</div>
                </div>
                <button type="button" className="btn btn-primary admin-bank-guide-action" onClick={() => void saveSwedish()}
                  disabled={busy !== null} data-testid="bank-guide-save-sv">
                  {busy === 'sv' && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                  {t('adminBankGuide.saveSwedish')}
                </button>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card className="h-100">
                <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-3">
                  <h2 className="h6 fw-bold mb-0">{t('adminBankGuide.english')}</h2>
                  <EnglishStatusBadge status={bank.guidanceEnStatus} />
                </div>
                {status === 'stale' && (
                  <div className="alert alert-warning" role="status" data-testid="bank-guide-stale">
                    {t('adminBankGuide.staleNotice', {
                      sv: dateTime(bank.guidanceSvUpdatedAt),
                      en: dateTime(bank.guidanceEnUpdatedAt)
                    })}
                  </div>
                )}
                {status === 'draft' && (
                  <div className="alert alert-info" role="status">
                    {bank.guidanceEnUpdatedAt ? t('adminBankGuide.draftOverPublished') : t('adminBankGuide.draftOnly')}
                  </div>
                )}
                {status === 'missing' && (
                  <div className="alert alert-secondary" role="status">{t('adminBankGuide.missingNotice')}</div>
                )}
                <div className="mb-3" lang="en">
                  <label className="form-label fw-semibold" htmlFor="bank-guide-en-text" lang={i18n.language}>{t('adminBankGuide.guidanceText')}</label>
                  <textarea id="bank-guide-en-text" className="form-control" rows={8} maxLength={4000}
                    value={english.guidanceTextEnDraft}
                    onChange={event => setEnglish({ ...english, guidanceTextEnDraft: event.target.value })} />
                </div>
                <div className="mb-3" lang="en">
                  <label className="form-label fw-semibold" htmlFor="bank-guide-en-path" lang={i18n.language}>{t('adminBankGuide.selfPath')}</label>
                  <textarea id="bank-guide-en-path" className="form-control" rows={2} maxLength={500}
                    value={english.raiseLimitSelfPathEnDraft}
                    onChange={event => setEnglish({ ...english, raiseLimitSelfPathEnDraft: event.target.value })} />
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-outline-primary admin-bank-guide-action" onClick={() => void saveDraft()}
                    disabled={busy !== null || !englishChanged} data-testid="bank-guide-save-draft">
                    {busy === 'en' && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('adminBankGuide.saveDraft')}
                  </button>
                  <button type="button" className="btn btn-primary admin-bank-guide-action" onClick={() => void publish()}
                    disabled={busy !== null || !canPublish} data-testid="bank-guide-publish">
                    {busy === 'publish' && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('adminBankGuide.publish')}
                  </button>
                </div>
              </Card>
            </div>
          </div>

          <Card className="mb-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h2 className="h6 fw-bold mb-0">{t('adminBankGuide.previewTitle')}</h2>
              <div className="btn-group" role="group" aria-label={t('adminBankGuide.previewLanguage')}>
                {(['sv', 'en'] as const).map(language => (
                  <button key={language} type="button"
                    className={`btn admin-bank-guide-action ${previewLanguage === language ? 'btn-primary' : 'btn-outline-primary'}`}
                    aria-pressed={previewLanguage === language} onClick={() => setPreviewLanguage(language)}
                    data-testid={`bank-guide-preview-${language}`}>
                    {t(`adminBankGuide.previewIn.${language}`)}
                  </button>
                ))}
              </div>
            </div>
            {preview && (
              <div className="admin-bank-guide-preview" data-testid="bank-guide-preview" lang={preview.language}>
                {preview.bank.fallbackLanguage && (
                  <div className="alert alert-warning" role="note">{t('adminBankGuide.previewFallback')}</div>
                )}
                <BankGuidanceCard bank={preview.bank} language={preview.language} />
              </div>
            )}
            <p className="small text-muted mt-3 mb-0">
              {t('adminBankGuide.previewNote', {
                amount: new Intl.NumberFormat('sv-SE').format(preview?.amount ?? 85000)
              })}
            </p>
          </Card>
        </>
      )}
    </DashboardLayout>
  );
}
